import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiMe, apiVerifyPayment } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import UnlockModal from './UnlockModal';

// Plain size designators, not routed through i18n — "1TB"/"2TB" already
// appear identically in both site languages everywhere else (hero badges,
// vault upgrade banner, etc.). Only the sentence around them is translated.
const PLAN_LABELS = { lifetime_1tb: '1TB', lifetime_2tb: '2TB' };

// 2026-08-30 rework — real bug found live: a customer who paid, then landed
// back here in a browser context with no cached unlock password (a fresh
// tab/webview — see the diagnosis: sessionStorage holding the derivation
// password isn't shared across tabs the way the login token in localStorage
// is), got auto-redirected to the vault after a flat 3s timer regardless of
// whether isAuthenticated had actually resolved yet. Since an unresolved
// session falls through App.js's routing to the anonymous homepage, someone
// who had JUST paid could end up looking at the public marketing page with
// no explanation — indistinguishable, from their side, from the payment
// having failed.
//
// Fixed by never leaving this component until the real auth state is known:
//   - no stored session at all: tell them the payment succeeded and offer a
//     login link, right here (spec: never fall through to the anon home).
//   - needs unlock / pending 2FA: show that UI on THIS page (reusing
//     UnlockModal directly, not a redirect elsewhere), with an explicit note
//     for the unlock case explaining WHY — otherwise "re-enter your
//     password" right after paying reads as "something went wrong".
//   - still silently restoring: a plain waiting state, no timer.
//   - only once isAuthenticated is actually true does the original
//     celebratory pause + redirect happen.
export function PaymentSuccessPage({ onDone, onLogout, onGoToLogin }) {
  const { t } = useTranslation('payment');
  const { refreshUser, restoring, needsUnlock, pending2fa, hasStoredSession, isAuthenticated } = useAuth();
  const [dots, setDots] = useState('');
  const [planLabel, setPlanLabel] = useState(null);
  const conversionFired = useRef(false);
  const redirected = useRef(false);
  const sessionId = new URLSearchParams(window.location.search).get('session_id');

  // Animated dots — harmless to run regardless of which state is showing.
  useEffect(() => {
    const timer = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(timer);
  }, []);

  // Conversion events (Google Ads + Meta Pixel + Reddit) — fetch order, fire
  // once per session. Unchanged from before: this never depended on auth
  // state, only on the Stripe session id in the URL.
  useEffect(() => {
    (async () => {
      if (!sessionId) return;
      const dedupKey = `gads_conv_${sessionId}`;
      let gia = false;
      try { gia = !!sessionStorage.getItem(dedupKey); } catch {}
      try {
        const res = await fetch(`/api/payments/session/${encodeURIComponent(sessionId)}`);
        if (!res.ok) return;
        const order = await res.json();
        // L'ETICHETTA DEL PIANO ARRIVA DA QUI (2026-09-12), non piu' solo da
        // /verify: questo endpoint non chiede il token, quindi funziona anche
        // per chi torna da Stripe passando dal ponte e una sessione non ce
        // l'ha. Si legge PRIMA dei pixel, e fuori dal controllo anti-doppione:
        // al secondo caricamento della stessa pagina i pixel non devono
        // ripartire, ma il cliente deve continuare a leggere cosa ha comprato.
        if (order?.plan && PLAN_LABELS[order.plan]) setPlanLabel(PLAN_LABELS[order.plan]);
        if (gia || conversionFired.current) return;
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-18155812869/EOarCJTYqa4cEIXwrtFD',
            value: order.amount,
            currency: order.currency,
            transaction_id: order.id,
          });
        }
        if (typeof window.fbq === 'function') {
          // order.metaEventId (not order.id) — the same event_id string the
          // webhook's server-side CAPI call already used, computed once by
          // metaConversions.purchaseEventId and just echoed back by this
          // endpoint, not recomputed here (2026-08-30, see that function's
          // comment). order.id stays as Google Ads' transaction_id above —
          // an unrelated, non-Meta identifier, not touched by this change.
          window.fbq('track', 'Purchase', { value: order.amount, currency: order.currency }, { eventID: order.metaEventId });
        }
        if (typeof window.rdt === 'function') {
          window.rdt('track', 'Purchase', { currency: order.currency, itemCount: 1, value: order.amount });
        }
        sessionStorage.setItem(dedupKey, '1');
        conversionFired.current = true;
      } catch {}
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  // Which plan they actually bought, for the unlock note and the
  // session-interrupted fallback — read from the real Stripe session via the
  // same authenticated endpoint used elsewhere (works even while
  // needsUnlock is true: the JWT is still valid, only the local encryption
  // key is missing — those are unrelated concepts). Never hand-typed.
  //
  // SOLO SE C'E' UNA SESSIONE (2026-09-12). /verify passa da authMiddleware:
  // senza token risponde 401, e chi paga attraverso il ponte (dal browser di
  // Facebook a Chrome) un token non ce l'ha per costruzione — il ponte porta
  // l'acquisto, non l'accesso. Chiamarlo comunque produceva un 401 garantito
  // proprio per chi arriva dagli annunci: oggi 2 richieste su 5. L'etichetta
  // adesso arriva dall'endpoint pubblico qui sopra; questa chiamata resta per
  // chi e' gia' dentro, perche' ha un secondo effetto che li' serve —
  // attivare il piano se il webhook non fosse ancora arrivato.
  useEffect(() => {
    if (!sessionId || !hasStoredSession) return;
    apiVerifyPayment(sessionId)
      .then(data => { if (data?.plan && PLAN_LABELS[data.plan]) setPlanLabel(PLAN_LABELS[data.plan]); })
      .catch(() => {});
  }, [sessionId, hasStoredSession]);

  // Only once isAuthenticated is genuinely true: refresh the user record
  // (plan is already activated server-side via webhook, this just makes
  // sure the client's copy agrees) then move on to the vault after a short,
  // real pause — never on a fixed timer that ignores auth state.
  useEffect(() => {
    if (!isAuthenticated || redirected.current) return;
    redirected.current = true;
    let cancelled = false;
    (async () => {
      try {
        const me = await apiMe();
        if (!cancelled && me?.user) refreshUser(me.user);
      } catch (err) {
        // Low-stakes on purpose (log only, no admin alarm — the plan was
        // already activated server-side via webhook regardless; this just
        // refreshes the client's local copy a little early, and the next
        // real page load would pick up the correct plan anyway) — but never
        // silent. console.error, not an empty catch.
        console.error('PaymentSuccessPage: failed to refresh user after payment', err.message);
      }
      if (cancelled) return;
      setTimeout(() => { if (!cancelled) onDone(); }, 1500);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [isAuthenticated]);

  // No token at all — the one case this page can't recover automatically.
  // The payment succeeded regardless of what the browser's storage looks
  // like right now; say so plainly and offer a way back in from here,
  // rather than silently falling through to the public homepage.
  if (!hasStoredSession) {
    // Email messa da parte dalla pagina-ponte (CheckoutBridgePage) quando si
    // e' pagato passando da un browser vero: qui non c'e' nessuna sessione
    // per costruzione — il ponte trasporta l'acquisto, non l'accesso — e
    // senza questo si dovrebbe ridigitare a mano l'indirizzo appena creato.
    let emailPonte = null;
    try { emailPonte = sessionStorage.getItem('ltc_email_ponte'); } catch {}
    return (
      <div className="payment-page">
        <div className="payment-card">
          <div className="payment-icon success">🎉</div>
          <h2>{t('success_title')}</h2>
          <p>{t('logged_out_after_payment_body', { planLabel: planLabel || t('your_plan_fallback') })}</p>
          {emailPonte && <p className="payment-email-ponte">{t('login_with_email', { email: emailPonte })}</p>}
          <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => onGoToLogin(emailPonte)}>
            {t('btn_login')}
          </button>
        </div>
      </div>
    );
  }

  // 2FA pending or password unlock needed — same component the rest of the
  // app uses for this, shown right here instead of navigating away.
  if (pending2fa) {
    return <UnlockModal onLogout={onLogout} />;
  }
  if (needsUnlock) {
    return (
      <UnlockModal
        onLogout={onLogout}
        contextNote={t('unlock_after_payment_note', { planLabel: planLabel || t('your_plan_fallback') })}
      />
    );
  }

  // Still silently restoring the session (or the one-tick window right
  // after mount before useAuth has determined anything yet) — wait, no
  // timer, no navigation.
  if (!isAuthenticated) {
    return (
      <div className="payment-page">
        <div className="payment-card">
          <div className="payment-icon success">🎉</div>
          <h2>{t('success_title')}</h2>
          <p>{t('verifying_session')}{dots}</p>
        </div>
      </div>
    );
  }

  // Authenticated — the real success screen.
  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className="payment-icon success">🎉</div>
        <h2>{t('success_title')}</h2>
        <p>{t('success_body')}<br/>{t('success_redirecting')}{dots}</p>
        <div className="payment-progress">
          <div className="payment-bar" style={{width:'100%', transition:'width 1.5s linear'}}/>
        </div>
        <button className="btn-primary" style={{marginTop:16}} onClick={onDone}>
          {t('btn_go_to_vault')}
        </button>
      </div>
    </div>
  );
}

export function PaymentCancelPage({ onDone }) {
  const { t } = useTranslation('payment');
  return (
    <div className="payment-page">
      <div className="payment-card">
        <div className="payment-icon">↩️</div>
        <h2>{t('cancel_title')}</h2>
        <p>{t('cancel_body')}</p>
        <button className="btn-primary" onClick={onDone}>{t('btn_back_to_vault')}</button>
      </div>
    </div>
  );
}
